/* eslint-disable */
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { authService } from '@/services/auth.service';

interface SessionMonitorOptions {
  onSessionInvalid?: () => void; // 세션 무효화 콜백
}

interface SessionResponse {
  ok: boolean;
  reason?: 'no_token' | 'not_found' | 'expired' | 'user_not_found' | 'error';
  user?: any;
}

interface SessionMonitorReturn {
  checkSession: () => Promise<void>;
  showSessionExpiredModal: boolean;
  handleSessionExpiredConfirm: () => void;
}

export function useSessionMonitor(options: SessionMonitorOptions = {}): SessionMonitorReturn {
  const { onSessionInvalid } = options;
  const router = useRouter();
  const isCheckingRef = useRef(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const hasShownModalRef = useRef(false); // 모달을 한 번만 표시하기 위한 플래그

  const checkSession = async () => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    try {
      const data = await authService.getSession();

      // To-Be 형식 처리
      if (data.code !== "SUCCESS") {
        // 세션이 무효화된 경우
        const reason = data.details?.reason;
        // console.log('[SessionMonitor] Session invalid (code !== SUCCESS), reason:', reason);
        
        // 다른 곳에서 로그인되어 세션이 무효화된 경우에만 모달 표시
        if ((reason === 'not_found' || reason === 'session_invalid') && !hasShownModalRef.current) {
          hasShownModalRef.current = true;
          setShowSessionExpiredModal(true);
          onSessionInvalid?.();
        }
      } else {
        // console.log('[SessionMonitor] Session is valid');
      }
    } catch (error) {
      console.error('[SessionMonitor] Error checking session:', error);
    } finally {
      isCheckingRef.current = false;
    }
  };

  useEffect(() => {
    // 페이지 포커스 시 세션 체크 (다른 탭에서 로그인했을 경우 감지)
    const handleFocus = () => {
      // console.log('[SessionMonitor] Page focused, checking session...');
      checkSession();
    };

    // 페이지 가시성 변경 시 세션 체크
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // console.log('[SessionMonitor] Page became visible, checking session...');
        checkSession();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onSessionInvalid, router]);

  const handleSessionExpiredConfirm = () => {
    setShowSessionExpiredModal(false);
    // 로그인 페이지로 리다이렉트
    if (typeof window !== 'undefined' && !window.location.pathname.includes('/auth/login')) {
      router.push('/auth/login?reason=session_expired');
    }
  };

  return {
    checkSession,
    showSessionExpiredModal,
    handleSessionExpiredConfirm,
  };
}

